import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  approvalsWaiting: "Approvals waiting",
  approvalsWaitingDescription: "Flows stopped at the compliance gate",
  requestedBy: (name: string, ago: string) => `Requested by ${name} · ${ago}`,
  openApprovals: "Open approvals",
  noApprovals: "No flows are waiting for approval.",
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    approvalsWaiting: "Approbations en attente",
    approvalsWaitingDescription: "Parcours bloqués au contrôle de conformité",
    requestedBy: (name, ago) => `Demandé par ${name} · ${ago}`,
    openApprovals: "Ouvrir les approbations",
    noApprovals: "Aucun parcours en attente d'approbation.",
  },
  es_ES: {
    approvalsWaiting: "Aprobaciones pendientes",
    approvalsWaitingDescription: "Flujos detenidos en el control de cumplimiento",
    requestedBy: (name, ago) => `Solicitado por ${name} · ${ago}`,
    openApprovals: "Abrir aprobaciones",
    noApprovals: "No hay flujos pendientes de aprobación.",
  },
  pt_BR: {
    approvalsWaiting: "Aprovações pendentes",
    approvalsWaitingDescription: "Fluxos parados na etapa de compliance",
    requestedBy: (name, ago) => `Solicitado por ${name} · ${ago}`,
    openApprovals: "Abrir aprovações",
    noApprovals: "Nenhum fluxo aguardando aprovação.",
  },
  de_DE: {
    approvalsWaiting: "Ausstehende Freigaben",
    approvalsWaitingDescription: "Flows, die an der Compliance-Freigabe angehalten wurden",
    requestedBy: (name, ago) => `Angefordert von ${name} · ${ago}`,
    openApprovals: "Freigaben öffnen",
    noApprovals: "Keine Flows warten auf Freigabe.",
  },
  it_IT: {
    approvalsWaiting: "Approvazioni in attesa",
    approvalsWaitingDescription: "Flussi fermi al controllo di compliance",
    requestedBy: (name, ago) => `Richiesto da ${name} · ${ago}`,
    openApprovals: "Apri approvazioni",
    noApprovals: "Nessun flusso in attesa di approvazione.",
  },
  ar: {
    approvalsWaiting: "موافقات بالانتظار",
    approvalsWaitingDescription: "مسارات متوقفة عند بوابة الامتثال",
    requestedBy: (name, ago) => `طلبها ${name} · ${ago}`,
    openApprovals: "فتح الموافقات",
    noApprovals: "لا توجد مسارات بانتظار الموافقة.",
  },
  ja_JP: {
    approvalsWaiting: "承認待ち",
    approvalsWaitingDescription: "コンプライアンス承認で停止中のフロー",
    requestedBy: (name, ago) => `依頼者：${name} · ${ago}`,
    openApprovals: "承認一覧を開く",
    noApprovals: "承認待ちのフローはありません。",
  },
  hi_IN: {
    approvalsWaiting: "लंबित अनुमोदन",
    approvalsWaitingDescription: "अनुपालन चरण पर रुके फ़्लो",
    requestedBy: (name, ago) => `${name} द्वारा अनुरोधित · ${ago}`,
    openApprovals: "अनुमोदन खोलें",
    noApprovals: "कोई फ़्लो अनुमोदन की प्रतीक्षा में नहीं है।",
  },
  zh_CN: {
    approvalsWaiting: "待审批",
    approvalsWaitingDescription: "停在合规审批环节的流程",
    requestedBy: (name, ago) => `申请人：${name} · ${ago}`,
    openApprovals: "打开审批",
    noApprovals: "没有等待审批的流程。",
  },
  zh_TW: {
    approvalsWaiting: "待核准",
    approvalsWaitingDescription: "停在合規核准關卡的流程",
    requestedBy: (name, ago) => `申請人：${name} · ${ago}`,
    openApprovals: "開啟核准項目",
    noApprovals: "沒有等待核准的流程。",
  },
  nl_NL: {
    approvalsWaiting: "Wachtend op goedkeuring",
    approvalsWaitingDescription: "Flows die bij de compliancecontrole zijn gestopt",
    requestedBy: (name, ago) => `Aangevraagd door ${name} · ${ago}`,
    openApprovals: "Goedkeuringen openen",
    noApprovals: "Er wachten geen flows op goedkeuring.",
  },
  nb_NO: {
    approvalsWaiting: "Venter på godkjenning",
    approvalsWaitingDescription: "Flyter stoppet ved compliance-kontrollen",
    requestedBy: (name, ago) => `Forespurt av ${name} · ${ago}`,
    openApprovals: "Åpne godkjenninger",
    noApprovals: "Ingen flyter venter på godkjenning.",
  },
  sv_SE: {
    approvalsWaiting: "Väntar på godkännande",
    approvalsWaitingDescription: "Flöden som stoppats vid compliance-kontrollen",
    requestedBy: (name, ago) => `Begärd av ${name} · ${ago}`,
    openApprovals: "Öppna godkännanden",
    noApprovals: "Inga flöden väntar på godkännande.",
  },
  fi_FI: {
    approvalsWaiting: "Odottaa hyväksyntää",
    approvalsWaitingDescription: "Compliance-tarkastukseen pysähtyneet prosessit",
    requestedBy: (name, ago) => `Pyytäjä ${name} · ${ago}`,
    openApprovals: "Avaa hyväksynnät",
    noApprovals: "Yksikään prosessi ei odota hyväksyntää.",
  },
  da_DK: {
    approvalsWaiting: "Afventer godkendelse",
    approvalsWaitingDescription: "Forløb stoppet ved compliance-kontrollen",
    requestedBy: (name, ago) => `Anmodet af ${name} · ${ago}`,
    openApprovals: "Åbn godkendelser",
    noApprovals: "Ingen forløb afventer godkendelse.",
  },
  is_IS: {
    approvalsWaiting: "Bíður samþykkis",
    approvalsWaitingDescription: "Ferli stöðvuð við regluvörslueftirlit",
    requestedBy: (name, ago) => `Beiðni frá ${name} · ${ago}`,
    openApprovals: "Opna samþykktir",
    noApprovals: "Engin ferli bíða samþykkis.",
  },
};
